/* eslint-disable jsx-a11y/alt-text */
'use client';

import type { ImageProps } from 'next/image';
import Image from 'next/image';
import type { UseSanityImageProps } from '@/components/SanityImage';
import { useSanityImage } from '@/components/SanityImage';

type Props = Omit<ImageProps, 'src'> &
  UseSanityImageProps & {
    objectFit?: 'contain' | 'cover' | 'scale-down';
    /** optional border radius */
    radius?: string;
    /** whether or not the image should be 100% width */
    fullWidth?: boolean;
  };

/**
 * A wrapper around Next.js' Image component that works with Sanity images.
 *
 * @param {SanityImageSource} src - The Sanity image source.
 * @param {number} width - The width of the image.
 *
 * The height is automatically calculated based on the aspect ratio of the image,
 * unless a height is provided.
 *
 * A placeholder is rendered if the image source has a low quality image placeholder (LQIP).
 *
 * @example
 * <SanityImage src={image} width={200} />
 */

export function SanityImage({
  objectFit = 'contain',
  style,
  src,
  width: widthProp,
  height: heightProp,
  aspectRatio: aspectRatioProp,
  radius,
  fullWidth = false,
  alt = '',
  ...other
}: Props) {
  const {
    defaultWidth,
    defaultHeight,
    height,
    imageSource,
    loader,
    lqip,
    width,
  } = useSanityImage({
    src,
    width: widthProp,
    height: heightProp,
    aspectRatio: aspectRatioProp,
  });

  if (!imageSource) {
    return null;
  }

  return (
    <Image
      {...other}
      blurDataURL={lqip}
      height={height}
      loader={loader}
      placeholder={lqip ? 'blur' : 'empty'}
      src={imageSource}
      style={{
        aspectRatio: fullWidth
          ? `${defaultWidth} / ${defaultHeight}`
          : undefined,
        width: fullWidth ? '100%' : width,
        height: 'auto',
        objectFit,
        borderRadius: radius,
        ...style,
      }}
      alt={alt}
      width={width}
    />
  );
}
